<template>
  <v-card outlined class="month-strip mb-4 pa-3">
    <div class="d-flex align-center mb-3 flex-wrap" style="gap: 8px">
      <span class="subtitle-1 font-weight-medium">
        {{ $t("proof.monthStrip.title") }}
      </span>
      <v-btn icon small @click="changeYear(-1)">
        <v-icon>mdi-chevron-left</v-icon>
      </v-btn>
      <span class="font-weight-medium">{{ year }}</span>
      <v-btn icon small @click="changeYear(1)">
        <v-icon>mdi-chevron-right</v-icon>
      </v-btn>
      <v-spacer />
      <v-chip small outlined color="primary" class="mr-1">
        {{ $t("proof.monthStrip.yearTotal") }}: {{ stats.totalCount }}
      </v-chip>
      <v-chip small outlined color="blue-grey">
        {{ formatCurrency(stats.totalCosts) }}
      </v-chip>
    </div>

    <div class="months">
      <v-card
        v-for="m in stats.months"
        :key="m.month"
        outlined
        class="month-card"
        :class="{ current: m.month === currentMonth, empty: !m.count }"
        @click="openMonth(m)"
      >
        <div class="month-name">{{ monthName(m.month) }}</div>
        <div class="month-count">
          <v-icon x-small class="count-icon">mdi-clipboard-check-outline</v-icon>
          {{ m.count }}
        </div>
        <div class="month-label">{{ $t("proof.monthStrip.countLabel") }}</div>
        <div class="month-cost">{{ formatCurrency(m.costs) }}</div>
      </v-card>
    </div>

    <v-dialog v-model="dialog.open" max-width="640">
      <v-card>
        <v-card-title class="headline">
          {{ monthName(dialog.month) }} {{ year }}
        </v-card-title>
        <v-card-text>
          <div v-if="dialog.loading" class="text-center py-6">
            <v-progress-circular indeterminate color="primary" />
          </div>
          <div
            v-else-if="!dialog.proofs.length"
            class="text--secondary text-body-2 py-2"
          >
            {{ $t("proof.monthStrip.none") }}
          </div>
          <v-list v-else dense>
            <v-list-item
              v-for="(p, i) in dialog.proofs"
              :key="p._id + '-' + i"
              @click="goToProof(p)"
            >
              <v-list-item-content>
                <v-list-item-title>{{ p.name }}</v-list-item-title>
                <v-list-item-subtitle>
                  {{ p.category && p.category.name ? p.category.name : "—" }}
                  · {{ formatDate(p.occurrenceDate || p.nextProofDate) }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-chip small outlined color="blue-grey">
                  {{ formatCurrency(p.costs) }}
                </v-chip>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialog.open = false">{{ $t("general.back") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
function emptyStats(year) {
  return {
    year,
    totalCount: 0,
    totalCosts: 0,
    months: Array.from({ length: 12 }, (_, i) => ({
      month: i + 1,
      count: 0,
      costs: 0,
    })),
  };
}

export default {
  name: "proofMonthStrip",
  data() {
    const year = new Date().getFullYear();
    return {
      year,
      currentMonth: new Date().getMonth() + 1,
      stats: emptyStats(year),
      dialog: { open: false, month: 1, proofs: [], loading: false },
    };
  },
  created() {
    this.fetchStats();
  },
  methods: {
    formatCurrency(n) {
      return (Number(n) || 0).toLocaleString(this.$i18n.locale, {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    },
    monthName(month) {
      return new Date(2000, month - 1, 1).toLocaleString(this.$i18n.locale, {
        month: "short",
      });
    },
    formatDate(val) {
      if (!val) return "—";
      return new Date(val).toLocaleDateString(this.$i18n.locale);
    },
    async fetchStats() {
      const res = await this.$store.dispatch("getProofMonthlyStats", this.year);
      this.stats = res && res.months ? res : emptyStats(this.year);
    },
    changeYear(delta) {
      this.year += delta;
      this.fetchStats();
    },
    async openMonth(m) {
      this.dialog.month = m.month;
      this.dialog.proofs = [];
      this.dialog.loading = true;
      this.dialog.open = true;
      const res = await this.$store.dispatch("getProofsByMonth", {
        year: this.year,
        month: m.month,
      });
      this.dialog.proofs = (res && res.data) || [];
      this.dialog.loading = false;
    },
    goToProof(p) {
      this.$router.push({
        name: "proofForm",
        params: { id: p._id || p.id, mode: "edit" },
      });
    },
  },
};
</script>

<style scoped>
.months {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 4px;
}
.month-card {
  flex: 1 0 76px;
  min-width: 76px;
  text-align: center;
  padding: 8px 4px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}
.month-card:hover {
  border-color: var(--v-primary-base);
}
.month-card.current {
  border-color: var(--v-primary-base);
  border-width: 2px;
}
.month-card.empty {
  opacity: 0.55;
}
.month-name {
  font-size: 0.72rem;
  text-transform: uppercase;
  color: #666;
}
.month-count {
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.2;
}
.count-icon {
  color: #90a4ae;
  vertical-align: baseline;
}
.month-label {
  font-size: 0.62rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #9e9e9e;
}
.month-cost {
  font-size: 0.72rem;
  color: #607d8b;
}
</style>
